import axios from 'axios';
import { StoreState } from "./store";
import jwt from "jwt-decode";
import { refreshTokenSuccess } from '../reducers/auth';
import { unauthorized } from '../reducers/unauthorized';

let store: StoreState;
export const injectStore = (_store: any) => store = _store;

export const axiosClient = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        'User-Agent-Front': 'Linze-360'
    }
});

// Refresh del token de sesión, single-flight: todos los requests que lo
// necesitan a la vez esperan el mismo POST. Devuelve el token nuevo o null.
let refreshPromise: Promise<string | null> | null = null;

const doRefresh = async (): Promise<string | null> => {
    const { refreshToken, user } = store.getState().auth;
    if (!refreshToken) return null;

    const response = await fetch(process.env.REACT_APP_API_TOKEN || "", {
        method: "POST",
        headers: { 'Authorization': `Bearer ${refreshToken}` },
    });
    if (response.status === 204) return null;

    const json = await response.json();
    if (!json.success) return null;

    const { data: { token, refresh_token, user: freshUser } } = json;
    const { exp }: any = jwt(token);

    store.dispatch({
        type: refreshTokenSuccess,
        payload: {
            token,
            // Sin refresh_token en la respuesta se conserva el vigente: sin él el
            // próximo refresh es imposible.
            refreshToken: refresh_token ?? refreshToken,
            expirationTime: exp,
            refreshType: "",
            user: freshUser ?? user,
        }
    });
    return token;
};

export const refreshSession = (): Promise<string | null> => {
    if (!refreshPromise) {
        refreshPromise = doRefresh()
            .catch(() => null)
            .finally(() => { refreshPromise = null; });
    }
    return refreshPromise;
};

// Antes cada request salía con el token del store tal cual, vencido o no, y el
// 401 que volvía deslogueaba al usuario (unauthorized resetea todo el estado).
// Ahora, si vence en menos de 30 s, se refresca antes de mandar.
const EXPIRY_MARGIN_S = 30;

export const isTokenExpiring = (): boolean => {
    if (!store) return false;
    const { token, refreshToken, expirationTime } = store.getState().auth;
    if (!token || !refreshToken || !expirationTime) return false;
    return Date.now() / 1000 >= Number(expirationTime) - EXPIRY_MARGIN_S;
};

axiosClient.interceptors.request.use(async (config: any) => {
    if (isTokenExpiring()) await refreshSession();

    const token = store.getState().auth.token;
    if (token) {
        config.headers = {
            ...(config.headers || {}),
            'Authorization': `Bearer ${token}`,
        };
        // Solo establecer Content-Type como application/json si no es FormData
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        }
    }
    return config;
}, (error) => Promise.reject(error));

axiosClient.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if (status !== 401) return Promise.reject(error);
        // Login con credenciales malas: es la respuesta correcta, no hay sesión que refrescar.
        if (originalRequest?.url === "users/access") return Promise.reject(error);

        // Un 401 puede ser solo el token vencido: se refresca y se reintenta UNA vez.
        // Si el refresh falla o el reintento vuelve 401, la sesión está muerta y se
        // cierra como antes.
        if (!originalRequest._retry) {
            originalRequest._retry = true;
            const fresh = await refreshSession();
            if (fresh) {
                originalRequest.headers = { ...(originalRequest.headers || {}), Authorization: `Bearer ${fresh}` };
                return axiosClient(originalRequest);
            }
        }

        store.dispatch({ type: unauthorized });
        return Promise.reject(error);
    }
);
